import { usePhotoUploader } from '@kaiizen/kai-pro-api-react-state';
import { getUniqueScanPhotos, type ScanPhoto } from '@kaiizen/kai-scan-template-system';
import { useCallback, useState } from 'react';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { useScanEditingContextSlice } from '@/Client/State';
import { usePhotoUploadProgressReporter } from '@/Client/State/PhotoUploadProgressContext';
import { ensureJpegFormat } from '@/lib/photoLib';

import type { ScanAudioAttachScope } from './scanAudioScopeLib';
import { captureVideoFrameAsJpegFile } from './videoFrameCaptureLib';
import { resolveCaptureDestinationDisplayName, type VideoToolboxScopeDisplayNames } from './videoToolboxActivityLogLib';
import { buildVideoFrameScanPhoto, resolveVideoToolboxFrameCaptureTarget } from './videoToolboxFramePhotoLib';

export type VideoToolboxPhotoCapturedEvent = {
  photoId: string;
  destinationDisplayName: string;
};

/** Captures the current video frame and uploads it through the standard scan-part photo pipeline. */
export const useVideoToolboxFrameCapture = (
  attachScope: ScanAudioAttachScope,
  scanVideoId: string | undefined,
  scopeDisplayNames?: VideoToolboxScopeDisplayNames,
  onPhotoCaptured?: (event: VideoToolboxPhotoCapturedEvent) => void,
) => {
  const photoUploader = usePhotoUploader('camera');
  const { scanDataMutators, scanId, scanIsEditable, scanRoot } = useScanEditingContextSlice([
    'scanDataMutators',
    'scanId',
    'scanIsEditable',
    'scanRoot',
  ]);
  const reportUploadProgress = usePhotoUploadProgressReporter();
  const [isCapturing, setIsCapturing] = useState(false);

  const canCapture = Boolean(
    scanIsEditable
      && scanRoot
      && scanVideoId
      && attachScope.groupId
      && !isCapturing,
  );

  const captureAndUpload = useCallback(async (video: HTMLVideoElement) => {
    if (!canCapture || !scanRoot || !scanVideoId) return;

    const target = resolveVideoToolboxFrameCaptureTarget(scanRoot, attachScope);
    if (!target) return;

    const destinationDisplayName = resolveCaptureDestinationDisplayName(attachScope, scopeDisplayNames);

    setIsCapturing(true);
    reportUploadProgress({ completed: 0, total: 1 });

    try {
      if (!video.paused) video.pause();

      const capturedFile = await captureVideoFrameAsJpegFile(video);
      const frameTimeSeconds = video.currentTime;
      const jpegFile = await ensureJpegFormat(capturedFile);

      let uploadedPhoto: ScanPhoto | undefined;
      await photoUploader(jpegFile, basePhoto => {
        uploadedPhoto = buildVideoFrameScanPhoto(basePhoto, scanVideoId, frameTimeSeconds);
      });

      if (!uploadedPhoto) throw new Error('Photo upload did not complete');

      await scanDataMutators.updateScanPart(target.scanPartIds, {
        photos: getUniqueScanPhotos(target.scanPart.photos, [uploadedPhoto]),
      });

      trackEvent(ANALYTICS_EVENTS.scanPhotosUploaded, {
        scanId,
        photoCount: 1,
        scope: target.scanPartIds.taskId ? 'task' : 'group',
      });
      onPhotoCaptured?.({ photoId: uploadedPhoto.photoId, destinationDisplayName });
      reportUploadProgress({ completed: 1, total: 1 });
    } catch (error) {
      alert(`Error capturing photo: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsCapturing(false);
      reportUploadProgress(null);
    }
  }, [
    attachScope,
    canCapture,
    onPhotoCaptured,
    photoUploader,
    reportUploadProgress,
    scanDataMutators,
    scanId,
    scanRoot,
    scanVideoId,
    scopeDisplayNames,
  ]);

  return { canCapture, captureAndUpload, isCapturing };
};
